import { useCallback, useEffect, useRef, useState } from 'react'
import { EventSourceInput } from '@fullcalendar/core'
import FullCalendar from '@fullcalendar/react'
import timegrid from '@fullcalendar/timegrid'
import { useToggle } from 'shared/hooks'

import { ButtonV2, Icon } from 'components'
import { EventCard } from './components'

import { DAY_OF_WEEK } from 'services/types'
import { WeekInterval } from './types'

import styles from './styles.module.scss'
import './Calendar.scss'

type DayHeaderProps = {
  numberOfEvents: number
  isCurrentDay: boolean
  isPast: boolean
  isFuture: boolean
  isSelected: boolean
  text: string
  date: Date
}

function DayHeader({
  numberOfEvents,
  isCurrentDay,
  isSelected,
  isFuture,
  isPast,
  text,
  date,
}: DayHeaderProps): JSX.Element {
  const parts = text.split(', ')
  const day = parts[1]
  let weekday = parts[0]
  weekday = weekday.charAt(0).toLowerCase() + weekday.slice(1)
  const month = String(date.getMonth() + 1)

  return (
    <div
      className={[
        styles.dayHeader,
        isCurrentDay && styles.dayHeaderDayToday,
        isPast && styles.dayHeaderDayPast,
        isFuture && styles.dayHeaderDayFuture,
        isSelected && styles.dayHeaderDaySelected,
      ].join(' ')}
    >
      <div className={styles.dayHeaderWeekday}>{weekday.slice(0, -1)}</div>
      <div className={styles.dayHeaderDay}>
        {day}/{month.length ? `0${month}` : month}
      </div>
      <div className={styles.numberOfEvents}>({numberOfEvents})</div>
    </div>
  )
}

type CalendarProps = {
  events: EventSourceInput
  scheduleDate?: number
  onChangeWeekInterval?: (interval: WeekInterval) => void
}

export function Calendar({
  events,
  scheduleDate,
  onChangeWeekInterval,
}: CalendarProps): JSX.Element {
  const calendarRef = useRef(null)
  const returnInitialWeekButton = useToggle()
  const [days, setDays] = useState<number[]>(Array(7).fill(0))
  const daysRef = useRef<number[]>(Array(7).fill(0))

  function restoreCalendar() {
    const calendarApi = calendarRef.current?.getApi()
    scheduleDate && calendarApi.gotoDate(new Date(scheduleDate))
  }

  const handleHeader = useCallback(
    (e) => (
      <DayHeader
        text={e.text}
        date={e.date}
        isPast={e.isPast}
        isFuture={e.isFuture}
        isSelected={e.date.getUTCDate() === new Date(scheduleDate).getUTCDate()}
        numberOfEvents={days[e.dow]}
        isCurrentDay={e.isToday}
      />
    ),
    [days, scheduleDate],
  )

  const getFirstMondayOfCurrentWeek = (): Date => {
    const today = new Date()
    const day = today.getDay()

    const diff = day === 0 ? -6 : 1 - day

    const monday = new Date(today)
    monday.setDate(today.getDate() + diff)
    monday.setHours(0, 0, 0, 0)
    return monday
  }

  useEffect(() => {
    const newDays = Array(7).fill(0)

    if (!!events.length) {
      events.forEach((event: EventSourceInput) => {
        const dayIndex = event?.start?.getDay()
        if (dayIndex >= 0 && dayIndex < 7) {
          newDays[dayIndex] = (newDays[dayIndex] || 0) + 1
        }
      })
    }

    if (JSON.stringify(newDays) !== JSON.stringify(daysRef.current)) {
      daysRef.current = newDays
      setDays(newDays)
    }
  }, [events])

  useEffect(() => {
    if (calendarRef.current && scheduleDate) {
      const calendarApi = calendarRef.current?.getApi()
      calendarApi.gotoDate(scheduleDate)
    }
  }, [scheduleDate])

  return (
    <>
      {returnInitialWeekButton.isVisible &&
        scheduleDate > getFirstMondayOfCurrentWeek().getTime() && (
          <ButtonV2
            appearance="secondary"
            color="element"
            onClick={restoreCalendar}
            className={styles.restoreButton}
          >
            <Icon name="back" color="element-default" /> Voltar à data
            selecionada
          </ButtonV2>
        )}
      <FullCalendar
        height="auto"
        locale="pt-BR"
        events={events}
        dayMaxEvents={6}
        ref={calendarRef}
        titleFormat={{
          month: 'long',
          year: 'numeric',
          separator: ' - ',
        }}
        slotMaxTime="21:00"
        slotMinTime="08:00"
        plugins={[timegrid]}
        allDayText="Período"
        scrollTime={{ hour: 0 }}
        slotDuration={{ hour: 1 }}
        dayHeaderContent={handleHeader}
        initialView="timeGridWeek"
        eventBorderColor="transparent"
        validRange={{ start: getFirstMondayOfCurrentWeek() }}
        hiddenDays={[DAY_OF_WEEK.SUNDAY]}
        datesSet={(dates) => {
          const newInterval: WeekInterval = {
            startDate: dates.start?.getTime(),
            endDate: dates.end?.getTime(),
          }
          onChangeWeekInterval?.(newInterval)

          const today = new Date()
          today.setHours(0, 0, 0, 0)

          if (scheduleDate) {
            scheduleDate >= newInterval.startDate &&
            scheduleDate <= newInterval.endDate
              ? returnInitialWeekButton.hide()
              : returnInitialWeekButton.show()
          }
        }}
        moreLinkText={(count) => `mais ${count}`}
        eventClassNames={styles.calendarEvent}
        viewClassNames={styles.calendarPeriod}
        dayCellClassNames={styles.calendarDayCell}
        allDayClassNames={styles.calendarSlotLabel}
        slotLabelClassNames={styles.calendarSlotLabel}
        dayHeaderClassNames={styles.calendarDayHeader}
        headerToolbar={{ start: '', end: 'prev title next' }}
        dayHeaderFormat={{ weekday: 'short', day: '2-digit' }}
        slotLabelFormat={{ hour: '2-digit', minute: '2-digit' }}
        eventContent={({ event }) => {
          return event.extendedProps?.status ? (
            <EventCard event={event} />
          ) : (
            <article className={styles.blockSchedule} />
          )
        }}
      />
    </>
  )
}
